import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { createTimer } from '../../util/timer.js';
import { useEventListener } from '../../util/useEventListener.js';
import useUniqueId from '../../util/useUniqueId.js';
import { Box } from '../Box/Box.js';

var _excluded = ["isLockFocus", "open", "autoFocus", "initialDelay", "onClose", "children", "focusOpenerElement"];
var candidatesSelector = ['[autofocus]', '[contenteditable]', '[role="button"]', '[tabindex]', 'a[href]', 'area[href]', 'audio[controls]', 'button:enabled', 'details', 'details>summary:first-of-type', 'embed', 'iframe', 'input:enabled', 'object', 'select:enabled', 'summary', 'textarea:enabled', 'video[controls]'].join(',');
var isFocusableElement = function isFocusableElement(e) {
  if (!e || e.hidden) {
    return false;
  }
  var disabledValue = e.getAttribute('disabled');
  if (disabledValue === '' || disabledValue === 'true') {
    return false;
  }
  var tabIndexValue = e.getAttribute('tabIndex');
  if (tabIndexValue !== null && parseInt(tabIndexValue, 10) < 0) {
    return false;
  }
  var ariaHiddenValue = e.getAttribute('aria-hidden');
  if (ariaHiddenValue === 'true') {
    return false;
  }
  return true;
};

// Must run in a different render cycle to nullify the effects of render, click events and transitions.
var delayedFocus = function delayedFocus(element) {
  var onFocusFailed = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var delay = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  var onFocused = arguments.length > 3 ? arguments[3] : undefined;
  if (!(element !== null && element !== void 0 && element.focus)) return;

  // The Modal components take 300ms to open using CSS transition.
  // It is complicated to capture the CSS transitionEnd event in Javascript
  // It is easier to use the initial delay for the workaround.
  var focusDelay = delay;
  createTimer({
    callback: function callback() {
      element.focus();
      // When failed to focus the element, remove the element from list of focusable elements.
      if (onFocusFailed && element !== document.activeElement) {
        onFocusFailed(element);
      }
      onFocused === null || onFocused === void 0 || onFocused();
    },
    delay: focusDelay
  });
};
var moveFocus = function moveFocus(e, elements) {
  var onFocusFailed = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var delay = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  if (!elements || elements.length === 0 || !(document.activeElement instanceof HTMLElement) || document.activeElement === null) {
    return;
  }
  var count = elements.length;
  var prevIndex = elements.indexOf(document.activeElement);
  var nextElementIndex = (e.shiftKey ? prevIndex + count - 1 : prevIndex + 1) % count;
  var nextElement = elements[nextElementIndex];
  delayedFocus(nextElement, onFocusFailed, delay);
  e.preventDefault();
};
var TabPressWithoutShiftEvent = new KeyboardEvent('keyboardEvent', {
  shiftKey: false,
  code: 'Tab'
});
var resolveCurrentElementIndex = function resolveCurrentElementIndex(focusableElements, isShiftKey) {
  var currentIndex = document.activeElement instanceof HTMLElement ? focusableElements.indexOf(document.activeElement) : -1;
  if (currentIndex === -1) {
    return isShiftKey ? focusableElements.length - 1 : 0;
  }
  return isShiftKey ? (currentIndex - 1 + focusableElements.length) % focusableElements.length : (currentIndex + 1) % focusableElements.length;
};
var handleIframeFocus = function handleIframeFocus(iframes, focusableElements, rootElement, onFocusFailed) {
  iframes.forEach(function (iframe) {
    try {
      var _iframe$contentWindow;
      // Try to access the iframe's contentWindow
      // This will throw an error if the iframe is cross-origin
      var iframeDoc = iframe.contentDocument || ((_iframe$contentWindow = iframe.contentWindow) === null || _iframe$contentWindow === void 0 ? void 0 : _iframe$contentWindow.document);
      if (iframeDoc) {
        // Add a blur event listener to the iframe's document
        var handleIframeBlur = function handleIframeBlur() {
          // When focus leaves the iframe, we need to check if it's going outside our trap
          createTimer({
            callback: function callback() {
              // If the active element is not in our trap, bring focus back
              if (document.activeElement && !rootElement.contains(document.activeElement) && focusableElements) {
                var iframeIndex = focusableElements.indexOf(iframe);
                if (iframeIndex !== -1) {
                  var nextIndex = (iframeIndex + 1) % focusableElements.length;
                  delayedFocus(focusableElements[nextIndex], onFocusFailed);
                } else {
                  // If we can't find the iframe, focus the first element
                  delayedFocus(focusableElements[0], onFocusFailed);
                }
              }
            },
            delay: 0
          });
        };

        // Add the event listener to the iframe's document
        iframeDoc.addEventListener('blur', handleIframeBlur, true);

        // Return a cleanup function
        return function () {
          iframeDoc.removeEventListener('blur', handleIframeBlur, true);
        };
      }
    } catch (error) {
      // Discard error for cross-origin iframes
    }
    return function () {};
  });
};
var FocusTrap = /*#__PURE__*/React.memo(function (_ref) {
  var isLockFocus = _ref.isLockFocus,
    open = _ref.open,
    autoFocus = _ref.autoFocus,
    initialDelay = _ref.initialDelay,
    onClose = _ref.onClose,
    children = _ref.children,
    _ref$focusOpenerEleme = _ref.focusOpenerElement,
    focusOpenerElement = _ref$focusOpenerEleme === void 0 ? true : _ref$focusOpenerEleme,
    rest = _objectWithoutProperties(_ref, _excluded);
  // This element opened the modal. We need the reference to element to focus this element after the modal is closed.
  var focusSource = document.activeElement;
  var rootRef = React.useRef(null);
  var focusableElements = React.useRef(null);
  var focusTrapId = useUniqueId();

  // Reference to the document-level event handlers
  var onFocusFailed = function onFocusFailed(element) {
    var _focusableElements$cu, _focusableElements$cu2;
    focusableElements.current = (_focusableElements$cu = (_focusableElements$cu2 = focusableElements.current) === null || _focusableElements$cu2 === void 0 ? void 0 : _focusableElements$cu2.filter(function (e) {
      return e !== element;
    })) !== null && _focusableElements$cu !== void 0 ? _focusableElements$cu : null;
  };

  // Function to handle tab key press
  var handleTabKey = React.useCallback(function (e) {
    if (!open || !isLockFocus || !focusableElements.current || focusableElements.current.length === 0) {
      return;
    }

    // Always prevent the default tab behavior
    e.preventDefault();
    var elements = focusableElements.current;
    var currentIndex = resolveCurrentElementIndex(elements, e.shiftKey);

    // Focus the next element
    var nextElement = elements[currentIndex];
    delayedFocus(nextElement, onFocusFailed);
  }, [open, isLockFocus, focusableElements]);
  var tabKeyHandler = React.useCallback(function (e) {
    if (e.key === 'Tab') {
      handleTabKey(e);
    }
  }, [handleTabKey]);

  // Function to handle focus events
  var handleFocusIn = React.useCallback(function (e) {
    if (!open || !isLockFocus || !rootRef.current || !focusableElements.current || focusableElements.current.length === 0) {
      return;
    }
    var target = e.target;

    // If the focus is within our trap, do nothing
    if (target !== null && 'nodeName' in target && rootRef.current.contains(target)) {
      return;
    }

    // If focus has moved outside our trap, bring it back
    e.preventDefault();

    // Focus the first or last element based on tab direction
    // We'll assume forward tabbing if we can't determine direction
    var elementToFocus = focusableElements.current[0];
    delayedFocus(elementToFocus, onFocusFailed);
  }, [open, isLockFocus, rootRef, focusableElements]);

  // Setup iframe handling
  React.useEffect(function () {
    var _focusableElements$cu3;
    if (!open || !isLockFocus || !rootRef.current) return;
    var iframes = rootRef.current.querySelectorAll('iframe');
    handleIframeFocus(iframes, (_focusableElements$cu3 = focusableElements.current) !== null && _focusableElements$cu3 !== void 0 ? _focusableElements$cu3 : [], rootRef.current, onFocusFailed);
  }, [open, isLockFocus, rootRef, focusableElements]);
  useEventListener({
    eventName: 'focusin',
    handler: handleFocusIn,
    attached: open && isLockFocus
  });
  useEventListener({
    eventName: 'keydown',
    handler: tabKeyHandler,
    attached: open && isLockFocus
  });
  React.useEffect(function () {
    // ensure that all ancestor elements until the focusTrap level don't have inert attribute
    var hasInertAncestor = function hasInertAncestor(element) {
      var parent = element.parentElement;
      while (((_parent = parent) === null || _parent === void 0 ? void 0 : _parent.id) !== focusTrapId) {
        var _parent, _parent2, _parent3;
        if ((_parent2 = parent) !== null && _parent2 !== void 0 && _parent2.hasAttribute('inert')) {
          return true;
        }
        parent = (_parent3 = parent) === null || _parent3 === void 0 ? void 0 : _parent3.parentElement;
      }
      return false;
    };
    var getFocusableElements = function getFocusableElements() {
      if (rootRef.current) {
        var allElements = rootRef.current.querySelectorAll(candidatesSelector);
        focusableElements.current = Array.from(allElements).filter(isFocusableElement);
        // if any of the ancestor has inert attribute, then it will not count as focusableElements
        focusableElements.current = focusableElements.current.filter(function (element) {
          return !hasInertAncestor(element);
        });
      }
    };

    // Must run to get list of Focusable elements at the initial stage
    getFocusableElements();
    var observer = new MutationObserver(getFocusableElements);
    if (rootRef.current) {
      observer.observe(rootRef.current, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }
    if (open && autoFocus && rootRef.current) {
      var isFocussedChildInFocusTrap = rootRef.current.contains(document.activeElement);
      // Do not autofocus when someone has programmatically set focused on any element within FocusTrap
      if (!isFocussedChildInFocusTrap && focusableElements.current && focusableElements.current.length > 0) {
        delayedFocus(focusableElements.current[0], onFocusFailed, initialDelay);
      }
    }
    return function () {
      observer.disconnect();
    };
  }, [rootRef, open, autoFocus, initialDelay, focusTrapId]);
  var handleKeyDown = React.useCallback(function (e) {
    if (!(e instanceof KeyboardEvent) || !open || e.code.toLocaleLowerCase() !== 'tab') {
      return;
    }
    var elements = focusableElements.current;
    if (!elements || elements.length === 0) {
      // When tab/shift-tab key down, browser will focus on its toolbar/url-input-box or focusable HTML element behind focus trap component.
      // Call prevent default to stop browser default focus behaviour.
      if (isLockFocus) {
        e.preventDefault();
      }
      return;
    }
    if (isLockFocus) {
      var _rootRef$current$cont, _rootRef$current;
      var isFocussedChildInFocusTrap = (_rootRef$current$cont = (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 ? void 0 : _rootRef$current.contains(document.activeElement)) !== null && _rootRef$current$cont !== void 0 ? _rootRef$current$cont : false;
      if (isFocussedChildInFocusTrap) {
        moveFocus(e, elements, onFocusFailed);
      } else {
        // If focus is outside the trap, bring it to the first or last element
        e.preventDefault();
        var targetIndex = e.shiftKey ? elements.length - 1 : 0;
        delayedFocus(elements[targetIndex], onFocusFailed);
      }
    }
  }, [rootRef, isLockFocus, open, focusableElements]);
  var attached = !(!isLockFocus && focusableElements.current);
  useEventListener({
    eventName: 'keydown',
    handler: handleKeyDown,
    attached: attached
  });
  React.useEffect(function () {
    if (!isLockFocus && focusableElements.current) {
      moveFocus(TabPressWithoutShiftEvent, focusableElements.current);
    }
    return function () {
      // Focus the element that opened the popup
      if (focusOpenerElement && focusSource !== null && focusSource instanceof HTMLElement) {
        delayedFocus(focusSource, null, 0, onClose);
      }
    };
    // Must not include `focusSource` in the dependencies
    // otherwise the focus return will break after closing the FocusTrap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleKeyDown, isLockFocus, onClose]);
  return /*#__PURE__*/React.createElement(Box, _extends({
    id: focusTrapId
  }, rest, {
    ref: rootRef,
    "aria-hidden": !open
  }), children);
});
FocusTrap.displayName = 'FocusTrap';
FocusTrap.defaultProps = {
  autoFocus: true,
  open: false,
  isLockFocus: true,
  initialDelay: 0
};

export { FocusTrap, candidatesSelector, isFocusableElement };
//# sourceMappingURL=FocusTrap.js.map
